import axios from '@/lib/axios'
import { useQuery } from '@tanstack/vue-query'

import type { PlansResponse } from '@/modules/plans/interfaces/plan'

interface Props {
	immediate?: boolean
}

const getPlans = async (): Promise<PlansResponse> => {
	const { data } = await axios.get<PlansResponse>('/api/v1/plans')
	return {
		// Normalize the feature value the same way useCompanySubscriptions does:
		// the catalog may expose it as `pivot.value`, but consumers (e.g.
		// PlanComparisonModal) JSON.parse `value` directly.
		plans: (data?.plans ?? []).map((plan) => ({
			...plan,
			features: (plan.features ?? []).map((feature) => ({
				...feature,
				value:
					(feature as { pivot?: { value?: string } }).pivot?.value ??
					feature.value ??
					'{}',
			})),
		})),
		featureMatrix: data?.featureMatrix ?? [],
	}
}

const usePlans = (props?: Props) => {
	const { immediate = true } = props || {}

	const { data, isLoading, isError, error, refetch, status } =
		useQuery<PlansResponse>(['plans'], getPlans, {
			enabled: immediate,
			retry: false,
			refetchOnWindowFocus: false,
			initialData: { plans: [], featureMatrix: [] },
		})

	return {
		data,
		isLoading,
		isError,
		error,
		refetch,
		status,
	}
}

export default usePlans
